import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
} from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import {
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";

let Mp3Recorder = null;

const initRecorder = async () => {
  if (!Mp3Recorder) {
    try {
      const MicRecorder = (await import("mic-recorder-to-mp3")).default;
      Mp3Recorder = new MicRecorder({ bitRate: 128 });
    } catch (error) {
      console.error("Failed to initialize recorder:", error);
      return null;
    }
  }
  return Mp3Recorder;
};

// dark colors follow WhatsApp Web's dark theme
const useStyles = makeStyles(theme => {
  const dark = theme.palette.type === "dark";
  const barColor = dark ? "#202c33" : "#f0f2f5";
  const iconColor = dark ? "#aebac1" : "#54656f";
  const fieldColor = dark ? "#2a3942" : "#fff";
  return {
  mainWrapper: {
    background: barColor,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background: barColor,
    width: "100%",
    display: "flex",
    padding: "5px 10px",
    minHeight: 62,
    boxSizing: "border-box",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: "9px 12px",
    margin: "0 10px",
    background: fieldColor,
    display: "flex",
    borderRadius: 8,
    flex: 1,
    position: "relative",
  },

  messageInput: {
    flex: 1,
    border: "none",
    fontSize: 15,
    lineHeight: "20px",
    color: dark ? "#d1d7db" : "#111b21",
  },

  sendMessageIcons: {
    color: iconColor,
  },

  uploadInput: {
    display: "none",
  },

  mediaPreviewStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    maxHeight: 220,
    overflowY: "auto",
    padding: "10px 13px 0",
    backgroundColor: barColor,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  mediaPreviewItem: {
    width: 96,
    height: 96,
    borderRadius: 6,
    overflow: "hidden",
    flex: "none",
    backgroundColor: fieldColor,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    "& img, & video": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
  },

  mediaPreviewFileName: {
    width: "100%",
    padding: "0 6px",
    fontSize: 11,
    textAlign: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },

  mediaCaptionWrapper: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    margin: "0 8px",
    padding: "4px 12px",
    background: fieldColor,
    borderRadius: 20,
  },

  mediaFileName: {
    flex: "none",
    maxWidth: "40%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginRight: 8,
    color: "grey",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: barColor,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: dark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: dark ? "#233138" : "#ffffff",
    padding: "2px",
    border: dark ? "1px solid #3b4a54" : "1px solid #CCC",
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          background: dark ? "#2a3942" : "#F1F1F1",
          cursor: "pointer",
        },
      },
    },
  },
  messageMentionsWrapper: {
    maxHeight: "240px",
    overflowY: "auto",
    zIndex: 10,
    "& li a.active": {
      background: dark ? "#2a3942" : "#F1F1F1",
    },
  },
  };
});

// lowercase and accent-free, to match what the user types
const normalizeSearch = text =>
  (text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// "@query" right before the cursor, at the start of the text or after a space
const findMention = (text, cursor) => {
  const match = /(^|\s)@([^\s@]*)$/.exec(text.slice(0, cursor));
  if (!match) return null;
  return { start: cursor - match[2].length - 1, query: match[2] };
};

// Outside a ticket route (conversations page) the caller passes getTicketId,
// resolved on each send, and resetKey to clear the input when the chat changes.
// isGroup enables the "@" mentions list.
const MessageInput = ({
  ticketStatus,
  getTicketId,
  resetKey,
  droppedFiles,
  isGroup,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { ticketId } = useParams();
  const resolveTicketId = async () =>
    getTicketId ? getTicketId() : ticketId;

  const [medias, setMedias] = useState([]);
  const [caption, setCaption] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [mention, setMention] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  // people picked from the list, kept while their "@Name" is still in the text
  const mentionsRef = useRef([]);
  const participantsLoadedRef = useRef(false);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setCaption("");
      setReplyingMessage(null);
      setMention(null);
      setParticipants([]);
      mentionsRef.current = [];
      participantsLoadedRef.current = false;
    };
  }, [ticketId, resetKey, setReplyingMessage]);

  useEffect(() => {
    if (droppedFiles?.files?.length && ticketStatus === "open") {
      setMedias(droppedFiles.files);
    }
  }, [droppedFiles, ticketStatus]);

  // thumbnails of the selected files, before sending
  const mediaPreviews = useMemo(
    () =>
      medias.map(file => ({
        file,
        url:
          file.type?.startsWith("image/") || file.type?.startsWith("video/")
            ? URL.createObjectURL(file)
            : null,
      })),
    [medias]
  );

  useEffect(
    () => () => mediaPreviews.forEach(p => p.url && URL.revokeObjectURL(p.url)),
    [mediaPreviews]
  );

  const loadParticipants = async () => {
    if (participantsLoadedRef.current) return;
    participantsLoadedRef.current = true;
    try {
      const { data } = await api.get(
        `/tickets/${await resolveTicketId()}/participants`
      );
      setParticipants(data);
    } catch (err) {
      participantsLoadedRef.current = false;
      toastError(err);
    }
  };

  const mentionOptions = useMemo(() => {
    if (!mention) return [];
    const query = normalizeSearch(mention.query);
    return participants
      .filter(
        p =>
          normalizeSearch(p.name).includes(query) || p.number.includes(query)
      )
      .slice(0, 30);
  }, [mention, participants]);

  const handleChangeInput = e => {
    const { value, selectionStart } = e.target;
    setInputMessage(value);
    handleLoadQuickAnswer(value);

    const found = isGroup ? findMention(value, selectionStart) : null;
    setMention(found);
    setMentionIndex(0);
    if (found) loadParticipants();
  };

  const handleSelectMention = participant => {
    const before = inputMessage.slice(0, mention.start);
    const after = inputMessage.slice(mention.start + 1 + mention.query.length);
    const inserted = `@${participant.name} `;
    const cursor = before.length + inserted.length;

    setInputMessage(`${before}${inserted}${after}`);
    setMention(null);
    if (!mentionsRef.current.some(m => m.id === participant.id)) {
      mentionsRef.current.push(participant);
    }

    setTimeout(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const handleMentionKeyDown = e => {
    if (!mention || mentionOptions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionIndex(i => (i + 1) % mentionOptions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionIndex(i => (i - 1 + mentionOptions.length) % mentionOptions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      handleSelectMention(mentionOptions[mentionIndex] || mentionOptions[0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMention(null);
    }
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });
    if (caption.trim()) {
      formData.append(
        "caption",
        signMessage ? `*${user?.name}:* ${caption.trim()}` : caption.trim()
      );
    }
    if (replyingMessage?.id) {
      formData.append("quotedMsgId", replyingMessage.id);
    }

    try {
      await api.post(`/messages/${await resolveTicketId()}`, formData);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
    setCaption("");
    setReplyingMessage(null);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? `*${user?.name}:* ${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    if (isGroup) {
      const mentions = mentionsRef.current.filter(m =>
        message.body.includes(`@${m.name}`)
      );
      if (mentions.length) message.mentions = mentions;
    }
    try {
      await api.post(`/messages/${await resolveTicketId()}`, message);
    } catch (err) {
      toastError(err);
    }

    mentionsRef.current = [];
    setMention(null);
    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();
      if (!recorder) {
        throw new Error("Recorder not available");
      }
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: inputMessage.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();
      if (!recorder) {
        throw new Error("Recorder not available");
      }
      const [, blob] = await recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `${new Date().getTime()}.mp3`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${await resolveTicketId()}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      const recorder = await initRecorder();
      if (recorder) {
        await recorder.stop().getMp3();
      }
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = event => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  if (medias.length > 0)
    return (
      <>
      <div className={classes.mediaPreviewStrip}>
        {mediaPreviews.map(({ file, url }, index) => (
          <div
            key={`${file.name}-${index}`}
            className={classes.mediaPreviewItem}
            title={file.name}
          >
            {url && file.type.startsWith("image/") && (
              <img src={url} alt={file.name} />
            )}
            {url && file.type.startsWith("video/") && (
              <video src={url} muted preload="metadata" />
            )}
            {!url && (
              <>
                <InsertDriveFileIcon className={classes.sendMessageIcons} />
                <span className={classes.mediaPreviewFileName}>
                  {file.name}
                </span>
              </>
            )}
          </div>
        ))}
      </div>
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <IconButton
          aria-label="cancel-upload"
          component="span"
          onClick={e => {
            setMedias([]);
            setCaption("");
          }}
        >
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>

        {loading ? (
          <div>
            <CircularProgress className={classes.circleLoading} />
          </div>
        ) : (
          <div className={classes.mediaCaptionWrapper}>
            <span className={classes.mediaFileName}>
              {medias.length > 1
                ? `${medias[0]?.name} +${medias.length - 1}`
                : medias[0]?.name}
            </span>
            <InputBase
              autoFocus
              fullWidth
              placeholder={i18n.t("messagesInput.captionPlaceholder")}
              value={caption}
              onChange={e => setCaption(e.target.value)}
              onKeyPress={e => {
                if (e.key === "Enter" && !loading) handleUploadMedia(e);
              }}
            />
          </div>
        )}
        <IconButton
          aria-label="send-upload"
          component="span"
          onClick={handleUploadMedia}
          disabled={loading}
        >
          <SendIcon className={classes.sendMessageIcons} />
        </IconButton>
      </Paper>
      </>
    );
  else {
    return (
      <Paper square elevation={0} className={classes.mainWrapper}>
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || recording || ticketStatus !== "open"}
              onClick={e => setShowEmoji(prevState => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={e => setShowEmoji(false)}>
                  <Picker
                    theme={theme.palette.type}
                    perLine={16}
                    showPreview={false}
                    showSkinTones={false}
                    onSelect={handleAddEmoji}
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || ticketStatus !== "open"}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                aria-label="upload"
                component="span"
                disabled={loading || recording || ticketStatus !== "open"}
              >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            <FormControlLabel
              style={{ marginRight: 7, color: "gray" }}
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={e => {
                    setSignMessage(e.target.checked);
                  }}
                  name="showAllTickets"
                  color="primary"
                />
              }
            />
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleOpenMenuClick}
            >
              <MoreVert></MoreVert>
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
            >
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                  onClick={e => setShowEmoji(prevState => !prevState)}
                >
                  <MoodIcon className={classes.sendMessageIcons} />
                </IconButton>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={loading || recording || ticketStatus !== "open"}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button">
                  <IconButton
                    aria-label="upload"
                    component="span"
                    disabled={loading || recording || ticketStatus !== "open"}
                  >
                    <AttachFileIcon className={classes.sendMessageIcons} />
                  </IconButton>
                </label>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <FormControlLabel
                  style={{ marginRight: 7, color: "gray" }}
                  label={i18n.t("messagesInput.signMessage")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={signMessage}
                      onChange={e => {
                        setSignMessage(e.target.checked);
                      }}
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              </MenuItem>
            </Menu>
          </Hidden>
          <div className={classes.messageInputWrapper}>
            <InputBase
              inputRef={input => {
                input && input.focus();
                input && (inputRef.current = input);
              }}
              className={classes.messageInput}
              placeholder={
                ticketStatus === "open"
                  ? i18n.t("messagesInput.placeholderOpen")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || ticketStatus !== "open"}
              onPaste={e => {
                ticketStatus === "open" && handleInputPaste(e);
              }}
              onKeyDown={handleMentionKeyDown}
              onKeyPress={e => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  if (mention && mentionOptions.length > 0) return;
                  handleSendMessage();
                }
              }}
            />
            {mention && mentionOptions.length > 0 && (
              <ul
                className={clsx(
                  classes.messageQuickAnswersWrapper,
                  classes.messageMentionsWrapper
                )}
              >
                {mentionOptions.map((participant, index) => (
                  <li key={participant.id}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a
                      className={index === mentionIndex ? "active" : undefined}
                      onMouseDown={e => {
                        e.preventDefault();
                        handleSelectMention(participant);
                      }}
                    >
                      {participant.name === participant.number
                        ? participant.name
                        : `${participant.name} - ${participant.number}`}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.messageQuickAnswersWrapperItem}
                      key={index}
                    >
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a onClick={() => handleQuickAnswersClick(value.message)}>
                        {`${value.shortcut} - ${value.message}`}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div></div>
            )}
          </div>
          {inputMessage ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={loading}
            >
              <SendIcon className={classes.sendMessageIcons} />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <IconButton
              aria-label="showRecorder"
              component="span"
              disabled={loading || ticketStatus !== "open"}
              onClick={handleStartRecording}
            >
              <MicIcon className={classes.sendMessageIcons} />
            </IconButton>
          )}
        </div>
      </Paper>
    );
  }
};

export default MessageInput;
